<template>
	<div class="participant">
		<div class="participant-info">
			<ProfilePic :user="user" size="28px" />

			<div class="participant-name">
				{{ displayName }}
				<div class="host-label" v-if="isHost">
					{{ $t('meetings_label_host') }}
				</div>
				<div class="host-label" v-if="isCoHost">
					{{ $t('meetings_label_cohost') }}
				</div>
			</div>
		</div>
		<button
			class="mute-btn"
			:title="buttonTitle"
			:class="{ enabled: canMute && !isMuted, 'own-mute': isMe }"
			@click="muteSingle"
			@keyup.enter="muteSingle"
			v-if="stream"
			:aria-label="
				$t('groupsettings_notifications_mute_action', [
					`${displayName} ${
						isHost ? $t('meetings_label_host') : $t('meetings_label_cohost')
					}`,
				])
			"
			tabindex="0"
		>
			<MicOffParticipantIcon v-if="isMuted" class="mic-off" />
			<MicOnParticipantIcon v-if="!isMuted" class="mic-on" />
		</button>

		<ObservingEyeIcon
			v-if="!stream"
			class="eye"
			tabindex="0"
			:aria-label="`${displayName} ${
				isHost ? $t('meetings_label_host') : $t('meetings_label_cohost')
			}`"
		/>
	</div>
</template>

<script>
import {
	getUserByConnectionId,
	getCurrentUser,
	getHostUser,
	getIsCoHostByUserId,
	getSession,
	getConnectionById,
	getIsHostByUserId,
	getMyConnectionId,
	getStream,
} from '../data'
import { ProfilePic } from '@/components/common'
import { showAlert } from '../MeetingAlert'
import {
	MicOnParticipantIcon,
	MicOffParticipantIcon,
	ObservingEyeIcon,
} from '@/icons'

export const PREEMPTIVE_MUTE_WAIT = 2000

export default {
	name: 'Participant',

	components: {
		MicOnParticipantIcon,
		MicOffParticipantIcon,
		ObservingEyeIcon,
		ProfilePic,
	},

	props: {
		connectionId: { type: [Number, String] },
		streamId: { type: String },
	},

	data() {
		return {
			user: {},
			isCoHost: false,
			isHost: false,
			fakeMuted: false,
			stream: {},
		}
	},

	emits: ['isMuted'],

	mounted() {
		this.$nextTick(() => {
			this.updateUser()
			this.stream = getStream(this.streamId)
		})

		this.$onEventBus({
			streamUpdate: (stream) => {
				if (this.streamId === stream?.id) {
					this.stream = { ...stream }
				}
			},
		})
	},

	methods: {
		updateUser() {
			// null for a connection with no user metadata (dial-in, archiving):
			// still a listed participant, just not one we can name (#898)
			this.user = getUserByConnectionId(this.connectionId) || {}

			if (!this.user.id) {
				this.isHost = false
				this.isCoHost = false
				return
			}

			const host = getHostUser()
			if (host && host.id === this.user.id) {
				this.isHost = true
			} else {
				this.isCoHost = getIsCoHostByUserId(this.user.id)
			}
		},

		muteSingle() {
			if ((this.isMuted && !this.isMe) || !this.canMute) return
			if (this.isMe) {
				// we're toggling our own audio
				return EventBus.$emit(this.isMuted ? 'meetingUnmute' : 'meetingMute')
			}
			// otherwise, send signal to this connection that they
			// need to mute themself
			const session = getSession()
			const to = getConnectionById(this.connectionId)
			session.signal({ type: 'mute', data: '', to }, (error) => {
				if (error) {
					console.error(error)
					showAlert({
						text: this.$t('meetings_unable_mute_error'),
						error: true,
					})
				}
			})
			// preemptively show the user as muted
			this.fakeMuted = true
			setTimeout(() => {
				this.fakeMuted = false
			}, PREEMPTIVE_MUTE_WAIT)
		},
	},

	watch: {
		connectionId() {
			this.updateUser()
		},

		streamId() {
			this.stream = getStream(this.streamId)
		},
	},

	computed: {
		// a connection with no user metadata (dial-in, archiving) is listed
		// like any other participant, just without a name to show (#898)
		displayName() {
			return this.user?.fullname || this.$t('chat_message_unknownuser')
		},

		isMuted() {
			return this.fakeMuted || !this.stream || !this.stream.hasAudio
		},

		canMute() {
			const user = getCurrentUser()
			return getIsHostByUserId(user.id) || getIsCoHostByUserId(user.id)
		},

		isMe() {
			return getMyConnectionId() === this.connectionId
		},

		buttonTitle() {
			if (this.canMute && this.isMe)
				return this.isMuted
					? this.$t('meetings_bottomtoolbar_audiooff')
					: this.$t('meetings_bottomtoolbar_audioon')

			if (this.canMute && !this.isMe)
				return this.isMuted
					? this.$t('groupsettings_notifications_title_muted')
					: this.$t('meetings_bottomtoolbar_audioon')

			return this.isMuted
				? this.$t('groupsettings_notifications_title_muted')
				: this.$t('groupsettings_notifications_title_unmuted')
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$avatar-size: 28px;
$color: #9c9a9a;

.participant {
	font-family: 'Avenir-Medium';
	font-size: 14px;
	color: $color;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;
	.participant-info {
		display: flex;
		align-items: center;
		.participant-name {
			margin-left: 8px;
			white-space: nowrap;
			max-width: 160px;
			text-overflow: ellipsis;
			overflow: hidden;
			.host-label {
				font-size: 10px;
			}
		}
	}
}
.mute-btn {
	outline: none;
	background: #222;
	cursor: default;
	&.enabled {
		cursor: pointer;
		margin-right: 2px;
	}
	&.own-mute {
		cursor: pointer;
	}
}
</style>
